import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function QuickStart() {
  return (
    <>
      {/* Hero */}
      <section className="page-hero">
        <HeroVideo className="page-hero-video" src={VIDEOS.meatSteak} />
        <div className="page-hero-overlay" />
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Free Resource</span>
            <h1>Quick Start Guide</h1>
            <p>
              Your first steps toward transformation
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="section">
        <div className="container">
          <div className="content-narrow mx-auto-narrow">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p className="text-lead mb-xl">
                Ready to begin? This guide gives you the essential steps to start your carnivore journey.
                For the complete approach—including detailed protocols, troubleshooting, and the full stories
                of transformation—get the book.
              </p>

              {/* Disclaimer Box */}
              <div className="quickstart-disclaimer-box">
                <p className="quickstart-disclaimer-text">
                  <strong>Important:</strong> Consult with your healthcare provider before making any dietary changes,
                  especially if you have pre-existing health conditions or take medications.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Step 1 */}
      <section className="section section-light">
        <div className="container">
          <div className="content-narrow mx-auto-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="quickstart-step-header">
                <div className="quickstart-step-number">1</div>
                <h2 className="quickstart-step-title">Clean Out Your Kitchen</h2>
              </div>
              <p>
                Remove temptations and make room for your new way of eating. This isn't about willpower—it's about environment design.
              </p>
              <h3>Remove:</h3>
              <ul>
                <li>Processed foods and snacks</li>
                <li>Sugary foods and beverages</li>
                <li>Grains and grain-based products</li>
                <li>Vegetable and seed oils</li>
                <li>Most packaged foods</li>
              </ul>
              <h3>Keep or Add:</h3>
              <ul>
                <li>Beef (ground beef, steaks, roasts)</li>
                <li>Other ruminant meats (lamb, bison)</li>
                <li>Eggs</li>
                <li>Butter and animal fats</li>
                <li>Salt</li>
                <li>Water</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Step 2 */}
      <section className="section">
        <div className="container">
          <div className="content-narrow mx-auto-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="quickstart-step-header">
                <div className="quickstart-step-number">2</div>
                <h2 className="quickstart-step-title">Start Simple</h2>
              </div>
              <p>
                Don't overcomplicate it. The simplest version of carnivore is the most effective starting point.
              </p>
              <h3>The Basic Framework:</h3>
              <ul>
                <li><strong>Eat meat when hungry</strong> — Don't restrict quantity initially</li>
                <li><strong>Prioritize fatty cuts</strong> — Fat is your energy source now</li>
                <li><strong>Salt to taste</strong> — You may need more than you think</li>
                <li><strong>Drink water when thirsty</strong> — Listen to your body</li>
                <li><strong>Eat until satisfied</strong> — Not stuffed, but genuinely full</li>
              </ul>
              <p>
                <strong>Pro tip:</strong> Ground beef is economical and easy to prepare. It's a perfect staple as you begin.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Step 3 */}
      <section className="section section-light">
        <div className="container">
          <div className="content-narrow mx-auto-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="quickstart-step-header">
                <div className="quickstart-step-number">3</div>
                <h2 className="quickstart-step-title">Expect an Adjustment Period</h2>
              </div>
              <p>
                Your body is transitioning from burning sugar to burning fat. This takes time, and you may experience some temporary discomfort.
              </p>
              <h3>What You Might Experience (Week 1-2):</h3>
              <ul>
                <li>Fatigue or low energy</li>
                <li>Headaches</li>
                <li>Digestive changes</li>
                <li>Cravings for old foods</li>
                <li>Irritability</li>
              </ul>
              <h3>What Helps:</h3>
              <ul>
                <li><strong>Electrolytes</strong> — Salt, magnesium, potassium</li>
                <li><strong>Adequate fat</strong> — Don't go too lean</li>
                <li><strong>Patience</strong> — Give your body time to adapt</li>
                <li><strong>Rest</strong> — Don't push hard workouts initially</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Step 4 */}
      <section className="section">
        <div className="container">
          <div className="content-narrow mx-auto-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="quickstart-step-header">
                <div className="quickstart-step-number">4</div>
                <h2 className="quickstart-step-title">Track Your Progress</h2>
              </div>
              <p>
                Document your journey. You'll want to look back and see how far you've come.
              </p>
              <h3>Consider Tracking:</h3>
              <ul>
                <li>How you feel each day (energy, mood, mental clarity)</li>
                <li>Sleep quality</li>
                <li>Any symptoms or changes</li>
                <li>What you're eating</li>
                <li>Before photos and measurements (optional but valuable)</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Next */}
      <section className="section section-light">
        <div className="container">
          <div className="content-narrow mx-auto-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2>What's Next?</h2>
              <p>
                This quick start guide gives you the basics, but there's so much more to learn. The full book includes:
              </p>
              <ul>
                <li>Complete transformation stories to inspire and guide you</li>
                <li>Detailed troubleshooting for common challenges</li>
                <li>The Nano Fasting protocol for enhanced results</li>
                <li>30-day tracking guides and worksheets</li>
                <li>Access to our private community</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta py-responsive">
        <div className="container text-center">
          <h2 className="quickstart-cta-heading">Ready for the Complete Guide?</h2>
          <p className="quickstart-cta-body">
            Get the full books, all the bonuses, and join the community.
          </p>
          <Link to="/join" className="btn btn-secondary btn-lg">
            Get Full Access
          </Link>
          <p className="cta-footer-text">
            $97 one-time · Lifetime access
          </p>
        </div>
      </section>
    </>
  );
}
